import React from 'react';
import styled, { css } from 'styled-components';

import Title from '../title';
import { Box, Flex, Paragraph } from '../UIKit/system';
import Button from '../button';
import mq from '../../utils/mq';
import Responsive from '../responsive';
import { getThemeColor } from '../../utils/styles';

const ShapesContainer = styled.div``;

const ShapesUpperContainer = styled.div`
  display: flex;
  justify-content: space-between;
  ${mq.mobile(css`
    flex-direction: column;
  `)};
`;

const StyledTitle = styled(Title)`
  width: 200px;
  font-size: 24px;
  line-height: 38px;
  ${mq.mobile(css`
    font-size: 16px;
    line-height: 26px;
    margin-bottom: 2px;
  `)};
`;

const StyledParagraph = styled(Paragraph)`
  width: 360px;
  ${mq.mobile(css`
    font-size: 14px;
    line-height: 24px;
    width: 100%;
    margin-bottom: 30px;
  `)};
`;

const ShapesTableContainer = styled.div`
  padding-top: 60px;
  ${mq.mobile(css`
    padding-top: 30px;
    overflow-x: auto;
  `)};
`;

const ShapesTable = styled.table`
  position: relative;
  width: 100%;
  border-collapse: collapse;
  text-align: right;
  margin-bottom: 16px;

  th {
    width: 20%;
    font-weight: bold;
    font-size: 14px;

    color: #161616;
    border-width: 0px;
    border-top-width: 0px;

    ${(props) =>
      props.left &&
      css`
        text-align: left;
      `};
    ${mq.mobile(css`
      font-size: 14px;
      text-align: left;
      white-space: nowrap;
      min-width: fit-content;
      padding-right: 15px;
    `)};
  }

  thead {
    background-color: ${getThemeColor('white')};
    box-shadow: 0 5px 15px ${getThemeColor('white')};
    height: fit-content;
    position: sticky;
    top: 0;
    z-index: 1;
  }

  tr {
    width: 20%;
    font-size: 16px;
    line-height: 28px;
    letter-spacing: -0.02em;
    color: #666666;
    border-width: 0px;
    border-top-width: 1px;
    border-style: solid;
    border-color: #ebebeb;
    &:first-of-type {
      border-top-width: 0px;
    }
    ${mq.mobile(css`
      font-size: 12px;
      text-align: left;
    `)};
  }

  td {
    padding: 10px 0px;
    border-color: #ebebeb;

    ${(props) =>
      props.left &&
      css`
        text-align: left;
      `};
  }
`;

const LeftTh = styled.th`
  text-align: left;
`;

const LeftTd = styled.td`
  text-align: left;
`;

const ViewMore = styled.a`
  font-size: 16px;
  line-height: 28px;
  letter-spacing: -0.02em;
  text-decoration-line: underline;
  color: #666666;
  width: 100%;
  text-align: left;
  padding-top: 16px;
  ${mq.mobile(css`
    font-size: 14px;
  `)};
`;

const StyledButton = styled(Button)`
  color: #161616;
  border-color: #d4d4d4 !important;
  width: 192px;
`;

const TableContainer = styled.div.attrs((props) => ({
  children: <div className="tableContainerInner">{props.children}</div>,
}))`
  ${(props) => props.scrollable && css`
    max-height: 585px;

    thead th {
      position: relative;

      &::after {
        border-bottom: 2px ${getThemeColor('grey2')} solid;
        bottom: 0;
        content: '';
        display: block;
        position: absolute;
        width: 100%;
      }
    }

    &::after {
      background-color: ${getThemeColor('grey2')};
      content: '';
      display: block;
      height: 2px;
      width: 100%;
    }
  `}

  .tableContainerInner {
    align-items: start;
    display: grid;
    position: relative;
    padding-right: 12px;
    width: calc(100% + 20px);

    @-moz-document url-prefix() {
      padding-right: 20px;
    }

    ${(props) => props.scrollable && css`
      overflow-x: hidden;
      overflow-y: scroll;
      max-height: 585px;

      @-moz-document url-prefix() {
        scrollbar-width: auto;
        scrollbar-color: ${getThemeColor('primary')} transparent;
      }

      &::-webkit-scrollbar {
        width: 8px;
      }

      &::-webkit-scrollbar-track {
        border-radius: 10px;
        background: ${getThemeColor('grey3')};
      }

      &::-webkit-scrollbar-track:hover {
        background: ${getThemeColor('grey3')};
      }

      &::-webkit-scrollbar-track:active {
        background: ${getThemeColor('grey3')};
      }

      &::-webkit-scrollbar-thumb {
        border-radius: 5px;
        background: ${getThemeColor('primary')};
      }

      &::-webkit-scrollbar-thumb:hover {
        background: ${getThemeColor('primary')};
      }

      &::-webkit-scrollbar-thumb:active {
        background: ${getThemeColor('primary')};
      }
    `}
  }
`

function shapes({ items, scrollable }) {
  let data = items[0];
  return (
    <ShapesContainer>
      {data && (
        <>
          <ShapesUpperContainer>
            <StyledTitle>{data.title && data.title}</StyledTitle>
            <StyledParagraph>
              {data.paragraph && data.paragraph}
            </StyledParagraph>
            {!data.hideFullSizeGuideCta && data.button && (
              <StyledButton {...data.button} transparent />
            )}
          </ShapesUpperContainer>
          <ShapesTableContainer>
            {data.largeDataMode ? (
              <>
                <TableContainer scrollable={scrollable && data.largeDataTable.length > 11}>
                  <ShapesTable>
                    <thead>
                      <tr>
                        <LeftTh>{data.largeDataColumnOneName}</LeftTh>
                        <th>{data.largeDataColumnTwoName}</th>
                        <th>{data.largeDataColumnThreeName}</th>
                        <Responsive.DesktopOrTablet>
                          <th>{data.largeDataColumnFourName}</th>
                          <th>{data.largeDataColumnFiveName}</th>
                        </Responsive.DesktopOrTablet>
                      </tr>
                    </thead>

                    <tbody>
                      {data.largeDataTable.map((data, i) => (
                        <tr key={i}>
                          <LeftTd>{data.models}</LeftTd>
                          <td>{data.width}</td>
                          <td>{data.height}</td>
                          <Responsive.DesktopOrTablet>
                            <td>{data.area}</td>
                            <td>{data.weight}</td>
                          </Responsive.DesktopOrTablet>
                        </tr>
                      ))}
                    </tbody>
                  </ShapesTable>
                </TableContainer>
                <ViewMore href={data.sizeGuideFile.filename} target={'_blank'}>
                  View fill size guide for individual sizes
                </ViewMore>
              </>
            ) : (
              <TableContainer scrollable={scrollable && data.tableData.length > 11}>
                <ShapesTable>
                  <thead>
                    <tr>
                      <LeftTh>{data.columnOneName}</LeftTh>
                      <th>{data.columnTwoName}</th>
                      <th>{data.columnThreeName}</th>
                      <Responsive.DesktopOrTablet>
                        <th>{data.columnFourName}</th>
                        <th>{data.columnFiveName}</th>
                      </Responsive.DesktopOrTablet>
                    </tr>
                  </thead>

                  <tbody>
                    {data.tableData &&
                      data.tableData.map((data, i) => (
                        <tr key={i}>
                          <LeftTd>{data.type}</LeftTd>
                          <td>{data.width}</td>
                          <td>{data.height}</td>
                          <Responsive.DesktopOrTablet>
                            <td>{data.area}</td>
                            <td>{data.columnFive}</td>
                          </Responsive.DesktopOrTablet>
                        </tr>
                      ))}
                  </tbody>
                </ShapesTable>
              </TableContainer>
            )}
          </ShapesTableContainer>
        </>
      )}
    </ShapesContainer>
  );
}

export default shapes;
